"use client";

import * as React from "react";
import {
  Responsive,
  useContainerWidth,
  type Layout,
  type LayoutItem,
  type ResponsiveLayouts,
} from "react-grid-layout";
import "react-grid-layout/css/styles.css";

import styles from "./DashboardGrid.module.css";

type Layouts = ResponsiveLayouts;

export interface DashboardGridLayoutItem {
  i: string;
  x: number;
  y: number;
  w: number;
  h: number;
  minW?: number;
  minH?: number;
  maxW?: number;
  maxH?: number;
  static?: boolean;
}

export interface DashboardGridProps {
  children: React.ReactNode;
  layouts: Layouts;
  cols?: Record<string, number>;
  breakpoints?: Record<string, number>;
  rowHeight?: number;
  margin?: [number, number];
  editing?: boolean;
  onLayoutChange?: (current: DashboardGridLayoutItem[], all: Layouts) => void;
  className?: string;
  emptyState?: React.ReactNode;
}

function toExport(item: LayoutItem): DashboardGridLayoutItem {
  return {
    i: item.i,
    x: item.x,
    y: item.y,
    w: item.w,
    h: item.h,
    minW: item.minW,
    minH: item.minH,
    maxW: item.maxW,
    maxH: item.maxH,
    static: item.static,
  };
}

export function DashboardGrid({
  children,
  layouts,
  cols = { lg: 12, md: 10, sm: 6, xs: 2, xxs: 2 },
  breakpoints = { lg: 1200, md: 996, sm: 768, xs: 480, xxs: 0 },
  rowHeight = 60,
  margin = [12, 12],
  editing = false,
  onLayoutChange,
  className,
  emptyState,
}: DashboardGridProps) {
  const { containerRef, width, mounted } = useContainerWidth();

  const childArray = React.Children.toArray(children);
  if (childArray.length === 0 && emptyState) {
    return <div className={styles.empty}>{emptyState}</div>;
  }

  return (
    <div
      ref={containerRef}
      className={[styles.root, className].filter(Boolean).join(" ")}
      data-editing={editing}
    >
      {mounted ? (
        <Responsive
        width={width}
        layouts={layouts}
        cols={cols}
        breakpoints={breakpoints}
        rowHeight={rowHeight}
        margin={margin}
        dragConfig={{ enabled: editing, handle: "[data-drag-handle]", bounded: false, threshold: 3 }}
        resizeConfig={{ enabled: editing, handles: ["se"] }}
        onLayoutChange={(current: Layout, all: Layouts) => {
          onLayoutChange?.(current.map(toExport), all);
        }}
      >
        {children}
      </Responsive>
      ) : null}
    </div>
  );
}
